<template>
  <div class="advanced-dropdown position-relative">
    <label v-if="!!$props.label || $slots.label">
      <slot name="label">{{ $props.label }}</slot>
    </label>

    <b-dropdown
      v-bind="$attrs"
      v-on="$listeners"
      v-loading="$props.loading"
      :toggle-class="`form-control  form-control-${$props.size} text-left shadow-none`"
      menu-class="py-0"
      class="w-100"
      ref="dropdownRef"
      @hide="() => (activeTab = 'list')"
    >
      <template v-if="!!$slots['button-content']" v-slot:button-content>
        <slot name="button-content" />
      </template>

      <template v-slot:default="{ hide }">
        <SlideXLeftTransition origin="left" group>
          <AdvancedDropdownItems
            v-if="'list' === activeTab"
            key="list"
            @select="handleSelectItem"
          >
            <template v-if="$props.canAddNew" v-slot:top>
              <BaseButton
                size="sm"
                class="mb-2"
                @click.stop="() => (activeTab = 'add-new')"
                >Add New</BaseButton
              >
            </template>

            <template v-slot:default="{ options }">
              <slot v-bind="{ options, hide, handleSelectItem }" />
            </template>

            <template v-slot:item-extra="itemExtraSlotData">
              <slot name="item-extra" v-bind="itemExtraSlotData" />
            </template>

            <template v-slot:footer>
              <slot name="footer" v-bind="{ hide }" />
            </template>
          </AdvancedDropdownItems>

          <b-dropdown-group
            v-if="$props.canAddNew && 'add-new' === activeTab"
            key="add-new"
          >
            <slot
              name="add-new"
              v-bind="{ cancel: handleAddNewCancellation, hide }"
            />
          </b-dropdown-group>
        </SlideXLeftTransition>
      </template>
    </b-dropdown>
  </div>
</template>

<script>
import { defineComponent, ref, provide, toRefs } from '@vue/composition-api'
import { SlideXLeftTransition } from 'vue2-transitions'

import { BS_SIZES } from '@constants'

import { BaseButton } from '@components/base'

import { advancedDropdownInjectionKey } from './AdvancedDropdown.constants'
import AdvancedDropdownItems from './AdvancedDropdownItems.vue'

export default defineComponent({
  name: 'AdvancedDropdown',
  components: {
    SlideXLeftTransition,

    BaseButton,

    AdvancedDropdownItems
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      validator: (options) => {
        const isValid = options.every((option) => {
          const keys = Object.keys(option)

          return keys.includes('value') && keys.includes('text')
        })

        return isValid
      },
      default: () => [],
      required: true
    },
    size: {
      type: String,
      default: BS_SIZES.md,
      validator: (value) => BS_SIZES.includes(value)
    },
    label: String,
    multiple: Boolean,
    loading: Boolean,
    canAddNew: Boolean
  },
  emits: ['input', 'select'],
  setup(props, { emit }) {
    const { value, options, size } = toRefs(props)

    const activeTab = ref('list')

    // Reference to the dropdown component
    const dropdownRef = ref(null)


    const handleSelectItem = (selectedItem) => {
      const newValue = Array.from(props.value)

      const index = newValue.indexOf(selectedItem.value)

      if (props.multiple) {
        if (0 > index) {
          newValue.push(selectedItem.value)
        } else {
          newValue.splice(index, 1)
        }

        emit('input', newValue)
      } else {
        if (0 > index) {
          emit('input', [selectedItem.value])
        } else {
          emit('input', [])
        }
      }

      emit('select', 0 > index ? selectedItem : null)

      console.log(props.multiple)
      console.log(dropdownRef.value)

      // Close the dropdown after selecting an item
      if (dropdownRef.value && !props.multiple) {
        console.log('selection done')
        dropdownRef.value.hide() // Hide dropdown programmatically
      }

    }

    const handleAddNewCancellation = () => {
      activeTab.value = 'list'
    }

    provide(advancedDropdownInjectionKey, {
      value,
      options,
      size,

      activeTab
    })

    return {
      activeTab,

      handleSelectItem,

      handleAddNewCancellation,
      dropdownRef
    }
  }
})
</script>

<style scoped>
.advanced-dropdown >>> .dropdown-toggle {
  background-color: #fff;
  padding: 0.625rem 0.75rem;
  text-transform: none;
  position: relative;
}

.advanced-dropdown >>> .dropdown-toggle:disabled {
  background-color: #e9ecef;
}

.advanced-dropdown >>> .dropdown-toggle:hover {
  border-color: #e9ecef;
}

.advanced-dropdown >>> .dropdown-toggle:focus,
.advanced-dropdown >>> .dropdown-toggle:active,
.advanced-dropdown >>> .dropdown-toggle[aria-expanded='true'] {
  background-color: #fff;
  border-color: #1c4a50;
}

.advanced-dropdown >>> .dropdown-toggle::after {
  position: absolute;
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
}

.advanced-dropdown >>> .dropdown-menu {
  min-width: 300px;
  /* Scroll the MENU itself - the search bar inside is position-sticky top-0,
     so it stays pinned while the options scroll past it. Scrolling the inner
     list instead would give the menu and the list a scrollbar each. Without a
     max-height the list just overflows the menu and runs over the modal. */
  max-height: 320px;
  overflow-y: auto;
}

.advanced-dropdown >>> .el-loading-mask {
  background-color: rgba(255, 255, 255, 0.5) !important;
}

.advanced-dropdown >>> .el-loading-spinner {
  width: 24px;
  margin-top: -12px;
}

.advanced-dropdown >>> .el-loading-spinner .circular {
  width: 24px;
  height: 24px;
}

.advanced-dropdown >>> .position-sticky {
  z-index: 2;
}
</style>
